import {areSetsEqual} from '@benchling/js-utils';
import React, {useCallback, useMemo} from 'react';
import {compose} from 'redux';

import ModelTypeConstants from 'constants/ModelTypeConstants';
import getCanvasRenderer from 'notebook/canvas/getCanvasRenderer';
import {Document} from 'notebook/documents/DocumentConstants';
import DocumentEditorContext, {DocumentEditorContextData} from 'notebook/documents/DocumentEditorContext';
import DocumentStore from 'notebook/documents/DocumentStore';
import useDocument from 'notebook/documents/useDocument';
import {
  EditorPropertiesContext,
  EditorPropertiesContextInterface,
  NoteContext,
  SectionConfigurationContext,
} from 'notebook/notes/core-ui/NoteContext';
import {CidIdMap} from 'notebook/notes/NoteConstants';
import {NoteView} from 'notebook/notes/tabs/NoteViews';
import useDisplayOptionsState from 'settings/visibility-options/useDisplayOptionsState';
import {VisibilityOptions} from 'settings/visibility-options/VisibilityOptions';
import useStableValue from 'ui-library/hooks/useStableValue';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';

export type DocumentEditorProps = Props & ConnectedProps;

interface Props {
  id: string;
  documentParentId?: string;
  /**
   *
   * @param isLockedByPermissions True when editing is disabled due to:
   *   - Lack of write permissions (!permissions.writable)
   *   - Viewing a previous version (!isLatest)
   *   - Parent component locked the editor (locked prop)
   *   - Unsaved external changes exist (isDirty)
   *   NOTE: Does NOT include realtime editor locks (handled separately with default banner)
   * @param isReadOnlyMode True when the user has enabled read only mode via display options.
   * @param displayOptions The display options instance to use for toggling settings.
   * @returns Returns a JSX.Element to render as a banner above the document editor. Return null to use default
   * banner that shows when the document is locked by realtime.
   */
  renderBanner?: (
    isLockedByPermissions: boolean,
    isReadOnlyMode?: boolean,
    displayOptions?: VisibilityOptions
  ) => JSX.Element | null;
  locked?: boolean;
  disableInternalAutoSaver?: boolean;
  viewer?: boolean;
  disableFormatting?: boolean;
  onChange?: (wasServerUpdate: boolean, changedDocumentId: string) => void;
  customSaveHandler?: () => Promise<boolean>;
  hideCanvasTitle?: boolean;
  title?: string;
  updateTitle?: (newTitle: string) => Promise<void>;
  isVirtualizationEnabled?: boolean;
  hideTitleInDocument?: boolean;
  manageRealtimeUpdates?: boolean;
  fileTypeName?: string;
  tableNamespace?: string;
  relatedDocumentIds?: Array<string>;
  disableSplitscreen?: boolean;
  disablePerformanceIndicator?: boolean;
  disableProtectionIndicator?: boolean;
  withDisplayOptions?: boolean;
}

type ConnectedProps =
  | {
      document: Document;
      cidIdMap: CidIdMap;
    }
  | {document: undefined; cidIdMap: undefined};

const DocumentEditor: (props: DocumentEditorProps) => JSX.Element = ({
  document,
  cidIdMap,
  id,
  documentParentId,
  renderBanner,
  locked,
  disableInternalAutoSaver,
  onChange,
  viewer,
  disableFormatting,
  customSaveHandler,
  hideCanvasTitle,
  title,
  updateTitle,
  isVirtualizationEnabled,
  hideTitleInDocument,
  manageRealtimeUpdates = false,
  fileTypeName = ModelTypeConstants.DOCUMENT,
  tableNamespace,
  relatedDocumentIds,
  disableSplitscreen,
  disablePerformanceIndicator = false,
  disableProtectionIndicator = false,
  withDisplayOptions = false,
}) => {
  // Create a stablized version of the relatedDocumentIds array to avoid unnecessary re-renders
  const stableRelatedDocumentIds = useStableValue(relatedDocumentIds ?? [], (a, b) =>
    areSetsEqual(new Set(a), new Set(b))
  );
  const getRelatedDocumentIds = useCallback(() => stableRelatedDocumentIds, [stableRelatedDocumentIds]);

  const {
    getIdFromCid,
    historyManager,
    isLatest,
    isDirty,
    parentType,
    registerObjects,
    updateNote,
    restoreStateAndSelection,
  } = useDocument(
    id,
    disableInternalAutoSaver,
    onChange,
    customSaveHandler,
    manageRealtimeUpdates,
    getRelatedDocumentIds
  );

  const parentEditorProperties = EditorPropertiesContext.useContext();
  const isConfig = document?.isConfig ?? true; // Fallback logic duplicated in isConfiguration()
  const note = document?.documentVersion.entryVersion.note;
  const permissions = document?.permissions;
  const sectionConfiguration = document?.sectionConfiguration;
  const canvasConfiguration = document?.canvasConfiguration;
  const {displayOptions, showFormulas, highlightCustomColumns, readOnlyMode} =
    useDisplayOptionsState(withDisplayOptions);

  const canvasRenderer = useMemo(
    () => (canvasConfiguration ? getCanvasRenderer(canvasConfiguration, hideCanvasTitle) : undefined),
    [canvasConfiguration, hideCanvasTitle]
  );
  const noteContextValue = useMemo(
    () => ({
      note: note!,
    }),
    [note]
  );
  const editorPropertiesContextValue: EditorPropertiesContextInterface = useMemo(
    () => ({
      ...parentEditorProperties,
      fileId: id,
      isConfig,
      disableSplitscreen,
      autoHeightToolbar: true,
      disablePerformanceIndicator,
      disableProtectionIndicator,
      showFormulas,
      highlightCustomColumns,
    }),
    [
      parentEditorProperties,
      id,
      isConfig,
      disableSplitscreen,
      disablePerformanceIndicator,
      disableProtectionIndicator,
      showFormulas,
      highlightCustomColumns,
    ]
  );

  const printOptions = useMemo(() => [], []);
  const documentEditorContext = useMemo<DocumentEditorContextData>(
    () => ({
      documentParentId,
      documentName: document?.name,
    }),
    [documentParentId, document?.name]
  );

  if (!note || !permissions) {
    return <CenteredHorizontalThrobber />;
  }

  /* Editor is locked if any of the following conditions are true:
   * - !permissions.writable: User lacks write permissions on the document
   * - !isLatest: User is viewing a previous version (not the latest)
   * - locked: Parent component explicitly locked the editor (e.g., archived document)
   * - isDirty: Document has unsaved external changes that would be overwritten
   *
   *  Document can also be locked by readOnlyMode, which is voluntarily enabled by the user via display options
   */
  const isLockedByPermissions = Boolean(!permissions.writable || !isLatest || locked || isDirty);
  return (
    <SectionConfigurationContext.Provider value={sectionConfiguration!}>
      <DocumentEditorContext.Provider value={documentEditorContext}>
        <NoteContext.Provider value={noteContextValue}>
          <EditorPropertiesContext.Provider value={editorPropertiesContextValue}>
            <NoteView
              fileId={id}
              isConfig={isConfig}
              renderCanvas={canvasRenderer?.renderCanvas}
              renderCanvasTitle={canvasRenderer?.renderCanvasTitle}
              banner={renderBanner?.(isLockedByPermissions, readOnlyMode, displayOptions)}
              fileType={ModelTypeConstants.DOCUMENT}
              fileTypeName={fileTypeName}
              fullScreen={false}
              fullHeight={true}
              getIdFromCid={getIdFromCid}
              cidIdMap={cidIdMap}
              historyManager={historyManager}
              parentType={parentType}
              note={note}
              updateNote={updateNote}
              title={title}
              updateTitle={updateTitle}
              locked={isLockedByPermissions || readOnlyMode}
              restoreStateAndSelection={restoreStateAndSelection}
              printOptions={printOptions}
              registerObjects={registerObjects}
              showTableOfContents={false}
              showTimestamps={false}
              spellCheck={false}
              printLayout={false}
              showPrintMargins={false}
              permissions={permissions}
              viewer={Boolean(viewer)}
              disableFormatting={disableFormatting}
              focusedTableProvenanceUuid={document.focusedTableProvenanceUuid}
              tablesToInsertOnCreation={document.tablesToInsertOnCreation}
              customInsertableTables={document.customInsertableTables}
              isVirtualizationEnabled={isVirtualizationEnabled}
              hideTitle={hideTitleInDocument}
              tableNamespace={tableNamespace}
              disableSplitscreen={disableSplitscreen}
              displayOptions={displayOptions}
              userReadOnlyMode={readOnlyMode}
            />
          </EditorPropertiesContext.Provider>
        </NoteContext.Provider>
      </DocumentEditorContext.Provider>
    </SectionConfigurationContext.Provider>
  );
};

function mapDocumentStoreProps(documentStore: typeof DocumentStore, props: Props): ConnectedProps {
  const itemState = documentStore.itemSubstore.getItemState(props.id);
  return itemState
    ? {document: itemState.document, cidIdMap: itemState.cidIdMap}
    : {
        document: undefined,
        cidIdMap: undefined,
      };
}

export default compose(DocumentStore.connect(mapDocumentStoreProps))(DocumentEditor);
